/** @file RenderBuffers: the engine-agnostic triangle list every renderer consumes (frozen contract). */
import type { Blend } from '@pose2d/core/types';

/** Layout constants of the interleaved vertex stream and the draw/run tables. */
export const RENDER_LAYOUT = {
  /** 24 bytes per vertex: x, y, u, v (f32) + light RGBA8 (u32) + dark RGB8 (u32). */
  VERTEX_WORDS: 6,
  /** draws: page, blend, indexStart, indexCount, vertexStart, vertexCount */
  DRAW_STRIDE: 6,
  /** runs: slot, indexStart, indexCount */
  RUN_STRIDE: 3,
  /** Max vertices per skeleton (Uint16 indices). */
  MAX_VERTICES: 65535,
} as const;

export type { Blend };

export interface RenderBuffers {
  /** Interleaved vertex stream; skeleton space; y-up. Words 0..3 are x, y, u, v. */
  readonly vertexF32: Float32Array;
  /** Same ArrayBuffer as vertexF32; word 4 = light (bytes R,G,B,A), word 5 = dark (bytes R,G,B,0). */
  readonly vertexU32: Uint32Array;
  /** Global vertex indices; index order == draw order. */
  readonly indices: Uint16Array;
  /** DRAW_STRIDE per draw: maximal runs sharing (page, blend). */
  readonly draws: Int32Array;
  /** RUN_STRIDE per run: one run per slot with uniform color (only filled when built with splitRuns: 'slot'). */
  readonly runs: Int32Array;
  vertexCount: number;
  indexCount: number;
  drawCount: number;
  runCount: number;
  /** Bumped on any change; renderers skip uploads when unchanged. */
  version: number;
  /** Bumped only when indices or draws changed. */
  topologyVersion: number;
  /** minX, minY, maxX, maxY of the current vertices. */
  readonly bounds: Float32Array;
}
